import { Injectable } from '@angular/core';

// Message shape sent by the admin (SharedScreenComponent) over the
// RTCDataChannel and applied here on the target (SideNavComponent) side.
// Coordinates are normalized [0,1] against the admin's rendered video box;
// this service converts them to real pixel coordinates on the target's own
// window before dispatching any DOM event, which is what keeps positioning
// correct across different screen resolutions/zoom levels.
export interface RemoteControlEvent {
  type: 'mouseMove' | 'click' | 'dblclick' | 'contextmenu' | 'scroll' | 'keydown' | 'keyup' | 'typeText';
  x?: number;
  y?: number;
  button?: 'left' | 'right' | 'middle';
  deltaX?: number;
  deltaY?: number;
  key?: string;
  code?: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
  value?: string;
}

/**
 * Applies remote-control events received over the WebRTC RTCDataChannel to
 * this tab's DOM, on the TARGET side only.
 *
 * IMPORTANT - browser limitation this service deliberately works within (see
 * the investigation report for the full breakdown): everything here goes
 * through documented, non-privileged browser APIs (dispatchEvent / .click()
 * / scrollBy / direct value assignment). It can only ever affect THIS web
 * page. It cannot move the real OS cursor, cannot type into other
 * applications or browser tabs, cannot open native OS dialogs (file pickers,
 * the real right-click context menu), and literal KeyboardEvent replay does
 * NOT insert text into editable fields on its own - that is why 'typeText'
 * exists as its own event type instead of trying to fake individual
 * keystrokes for whole-field text entry. Single-character typing, Backspace,
 * Delete and Enter are handled as a best-effort direct value edit in
 * applyEditableKey() below; other non-printable keys (arrows, Tab, Escape,
 * ...) are dispatched as real KeyboardEvents only, which lets any
 * JS-implemented app shortcut react to them, but does not drive native
 * caret/focus navigation - the investigation report already flagged that as
 * a real browser limitation, not something faked here.
 */
@Injectable({ providedIn: 'root' })
export class RemoteControlInputService {
  private cursorEl: HTMLDivElement | null = null;
  private lastFocusedEditable: HTMLElement | null = null;

  /** Creates the on-screen virtual cursor overlay. Call when remote control becomes active. */
  showCursor(): void {
    if (this.cursorEl) return;
    const el = document.createElement('div');
    el.setAttribute('data-fyt-remote-cursor', 'true');
    el.style.position = 'fixed';
    el.style.top = '0';
    el.style.left = '0';
    el.style.width = '18px';
    el.style.height = '18px';
    el.style.borderRadius = '50%';
    el.style.background = 'rgba(255, 64, 64, 0.55)';
    el.style.border = '2px solid #ff4040';
    el.style.pointerEvents = 'none';
    el.style.zIndex = '2147483647';
    el.style.transform = 'translate(-9px, -9px)';
    el.style.transition = 'top 40ms linear, left 40ms linear';
    document.body.appendChild(el);
    this.cursorEl = el;
  }

  /** Removes the virtual cursor overlay. Call when remote control stops/pauses. */
  hideCursor(): void {
    if (this.cursorEl && this.cursorEl.parentNode) {
      this.cursorEl.parentNode.removeChild(this.cursorEl);
    }
    this.cursorEl = null;
  }

  /** Clears any tracked "currently focused via remote control" element. Call on stop/pause. */
  reset(): void {
    this.hideCursor();
    this.lastFocusedEditable = null;
  }

  apply(evt: RemoteControlEvent): void {
    if (!evt || !evt.type) return;
    switch (evt.type) {
      case 'mouseMove':
        this.handleMouseMove(evt);
        break;
      case 'click':
        this.handleClick(evt, false);
        break;
      case 'dblclick':
        this.handleClick(evt, true);
        break;
      case 'contextmenu':
        this.handleContextMenu(evt);
        break;
      case 'scroll':
        this.handleScroll(evt);
        break;
      case 'keydown':
        this.handleKey(evt, 'keydown');
        break;
      case 'keyup':
        this.handleKey(evt, 'keyup');
        break;
      case 'typeText':
        this.handleTypeText(evt);
        break;
      default:
        break;
    }
  }

  // DEBUG (temporary - remove once Remote Control is confirmed working):
  // describes the element document.elementFromPoint() actually resolved, so
  // it is visible whether coordinate mapping is landing on the right node
  // (or on nothing at all) rather than assuming the dispatched event found
  // its intended target.
  private describeElement(el: Element | null): string {
    if (!el) return 'null';
    const tag = el.tagName ? el.tagName.toLowerCase() : 'unknown';
    const id = (el as HTMLElement).id ? `#${(el as HTMLElement).id}` : '';
    const cls = (el as HTMLElement).className && typeof (el as HTMLElement).className === 'string'
      ? `.${(el as HTMLElement).className.trim().split(/\s+/).join('.')}`
      : '';
    return `${tag}${id}${cls}`;
  }

  private toPixels(x?: number, y?: number): { px: number, py: number } {
    // Defensive clamp - a malformed/out-of-range message never dispatches
    // an event outside the visible viewport.
    const nx = Math.min(1, Math.max(0, x ?? 0));
    const ny = Math.min(1, Math.max(0, y ?? 0));
    return { px: nx * window.innerWidth, py: ny * window.innerHeight };
  }

  private handleMouseMove(evt: RemoteControlEvent): void {
    const { px, py } = this.toPixels(evt.x, evt.y);
    if (this.cursorEl) {
      this.cursorEl.style.left = `${px}px`;
      this.cursorEl.style.top = `${py}px`;
    }
    const el = document.elementFromPoint(px, py);
    console.log('[RemoteControl] target element:', this.describeElement(el), 'at', px, py);
    if (el) {
      el.dispatchEvent(new MouseEvent('mousemove', {
        bubbles: true, cancelable: true, clientX: px, clientY: py
      }));
    }
  }

  private handleClick(evt: RemoteControlEvent, isDouble: boolean): void {
    const { px, py } = this.toPixels(evt.x, evt.y);
    const el = document.elementFromPoint(px, py) as HTMLElement | null;
    console.log('[RemoteControl] target element:', this.describeElement(el), 'at', px, py);
    if (!el) return;

    const buttonIndex = evt.button === 'right' ? 2 : evt.button === 'middle' ? 1 : 0;
    const commonInit: MouseEventInit = {
      bubbles: true, cancelable: true, clientX: px, clientY: py, button: buttonIndex, view: window
    };

    el.dispatchEvent(new MouseEvent('mousedown', commonInit));
    el.dispatchEvent(new MouseEvent('mouseup', commonInit));

    if (isDouble) {
      el.dispatchEvent(new MouseEvent('dblclick', commonInit));
    } else if (buttonIndex === 0) {
      // Prefer the native .click() method over a synthetic 'click' dispatch
      // for plain left-clicks: per the HTML spec, .click() still triggers an
      // element's default action (link navigation, checkbox toggle, form
      // submit) even when not a trusted user gesture, whereas dispatching a
      // MouseEvent('click') only fires listeners and does not reliably
      // trigger those native default actions in every browser.
      if (typeof (el as any).click === 'function') {
        (el as HTMLElement).click();
      } else {
        el.dispatchEvent(new MouseEvent('click', commonInit));
      }
    } else {
      el.dispatchEvent(new MouseEvent('click', commonInit));
    }

    // Track the element under a left-click as the "remote focus" target for
    // subsequent typeText/keydown text-insertion, and give it real focus so
    // any existing focus-based UI (e.g. floating labels) still looks right
    // to the person watching their own screen.
    if (buttonIndex === 0 && this.isEditable(el)) {
      this.lastFocusedEditable = el;
      el.focus();
    }
  }

  private handleContextMenu(evt: RemoteControlEvent): void {
    const { px, py } = this.toPixels(evt.x, evt.y);
    const el = document.elementFromPoint(px, py) as HTMLElement | null;
    console.log('[RemoteControl] target element:', this.describeElement(el), 'at', px, py);
    if (!el) return;
    // This can only reach a context menu FYTrack itself implements in JS
    // (a 'contextmenu' listener). It cannot open the browser/OS's native
    // right-click menu - no web API can summon that.
    el.dispatchEvent(new MouseEvent('contextmenu', {
      bubbles: true, cancelable: true, clientX: px, clientY: py, button: 2, view: window
    }));
  }

  private handleScroll(evt: RemoteControlEvent): void {
    // Native scroll APIs are not gated by "trusted event" rules, so this is
    // fully reliable, unlike click/keyboard.
    const deltaX = evt.deltaX ?? 0;
    const deltaY = evt.deltaY ?? 0;
    let scrollTarget: Element | Window = window;
    if (evt.x !== undefined && evt.y !== undefined) {
      const { px, py } = this.toPixels(evt.x, evt.y);
      const el = document.elementFromPoint(px, py);
      const scrollableAncestor = this.findScrollableAncestor(el);
      console.log('[RemoteControl] target element:', this.describeElement(el),
        '-> scrollable ancestor:', this.describeElement(scrollableAncestor), 'at', px, py);
      if (scrollableAncestor) scrollTarget = scrollableAncestor;
    }
    if (scrollTarget === window) {
      window.scrollBy({ left: deltaX, top: deltaY, behavior: 'auto' });
    } else {
      (scrollTarget as Element).scrollBy({ left: deltaX, top: deltaY, behavior: 'auto' } as ScrollToOptions);
    }
  }

  private findScrollableAncestor(el: Element | null): Element | null {
    let node: Element | null = el;
    while (node && node !== document.body) {
      const style = window.getComputedStyle(node);
      const overflowY = style.overflowY;
      const canScroll = (overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight;
      if (canScroll) return node;
      node = node.parentElement;
    }
    return null;
  }

  private handleKey(evt: RemoteControlEvent, type: 'keydown' | 'keyup'): void {
    if (!evt.key) return;
    const target = (this.lastFocusedEditable as HTMLElement) || (document.activeElement as HTMLElement) || document.body;
    console.log('[RemoteControl] target element:', this.describeElement(target),
      '(lastFocusedEditable:', this.describeElement(this.lastFocusedEditable),
      ', activeElement:', this.describeElement(document.activeElement), ')');

    // Dispatch the real KeyboardEvent regardless - this is what lets any
    // JS-implemented shortcut in the FYTrack app (an addEventListener-based
    // 'keydown' handler, which is what Angular's (keydown.enter)-style
    // bindings compile to) fire correctly. It will NOT, by itself, insert a
    // character into an editable field - see applyEditableKey() for that.
    target.dispatchEvent(new KeyboardEvent(type, {
      key: evt.key,
      code: evt.code || evt.key,
      bubbles: true,
      cancelable: true,
      ctrlKey: !!evt.ctrlKey,
      shiftKey: !!evt.shiftKey,
      altKey: !!evt.altKey,
      metaKey: !!evt.metaKey
    }));

    // Best-effort direct text editing for the common single-character /
    // Backspace / Delete / Enter cases, only on keydown, only when we have a
    // known editable target, and only when no modifier is held (so this
    // never fights with an app-level Ctrl/Alt/Meta shortcut a listener just
    // handled above).
    if (type === 'keydown' && this.isEditable(target) && !evt.ctrlKey && !evt.altKey && !evt.metaKey) {
      this.applyEditableKey(target, evt.key);
    }
  }

  private applyEditableKey(el: HTMLElement, key: string): void {
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      if (key.length === 1) {
        el.value = el.value.slice(0, start) + key + el.value.slice(end);
        const pos = start + 1;
        el.setSelectionRange(pos, pos);
      } else if (key === 'Backspace') {
        if (start === end && start > 0) {
          el.value = el.value.slice(0, start - 1) + el.value.slice(end);
          el.setSelectionRange(start - 1, start - 1);
        } else {
          el.value = el.value.slice(0, start) + el.value.slice(end);
          el.setSelectionRange(start, start);
        }
      } else if (key === 'Delete') {
        if (start === end && end < el.value.length) {
          el.value = el.value.slice(0, start) + el.value.slice(end + 1);
        } else {
          el.value = el.value.slice(0, start) + el.value.slice(end);
        }
        el.setSelectionRange(start, start);
      } else if (key === 'Enter' && el instanceof HTMLTextAreaElement) {
        el.value = el.value.slice(0, start) + '\n' + el.value.slice(end);
        el.setSelectionRange(start + 1, start + 1);
      } else {
        // Other non-printable keys (arrows, Tab, Escape, ...): the
        // KeyboardEvent dispatched above is all that's supported - native
        // caret/focus navigation via synthetic events is not reliably
        // driven by the browser.
        return;
      }
      // Fire the same event Angular/reactive-forms/ngModel listen for so
      // the framework picks up the programmatic value change.
      el.dispatchEvent(new Event('input', { bubbles: true }));
    } else if (el.isContentEditable) {
      if (key.length === 1) {
        document.execCommand('insertText', false, key);
      } else if (key === 'Backspace') {
        document.execCommand('delete', false);
      } else if (key === 'Enter') {
        document.execCommand('insertLineBreak', false);
      }
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  private handleTypeText(evt: RemoteControlEvent): void {
    // Whole-value text entry: sets the field's value directly and fires the
    // same 'input'/'change' events Angular's ngModel/reactive forms listen
    // for. This is the reliable path for typing a full string in one shot,
    // deliberately not implemented as a replay of individual KeyboardEvents.
    let el: HTMLElement | null = this.lastFocusedEditable;
    if (!el && evt.x !== undefined && evt.y !== undefined) {
      const { px, py } = this.toPixels(evt.x, evt.y);
      el = document.elementFromPoint(px, py) as HTMLElement;
    }
    console.log('[RemoteControl] target element:', this.describeElement(el));
    if (!el || evt.value === undefined) return;

    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      el.value = evt.value;
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (el.isContentEditable) {
      el.textContent = evt.value;
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  private isEditable(el: Element | null): el is HTMLElement {
    if (!el) return false;
    return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || (el as HTMLElement).isContentEditable;
  }
}
